import classNames from 'classnames';
import React, {
  cloneElement,
  forwardRef,
  isValidElement,
  useEffect,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Form } from 'react-bootstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter,
  useBlockLayout,
  useFlexLayout,
  type Column,
  type Row,
  type SortingRule,
} from 'react-table';

interface IndeterminateCheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
  className?: string;
}

export const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  IndeterminateCheckboxProps
>(({ indeterminate, className, ...rest }, ref) => {
  const defaultRef = useRef<HTMLInputElement>(null);
  const resolvedRef = (ref as React.RefObject<HTMLInputElement>) || defaultRef;

  useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [resolvedRef, indeterminate]);

  return (
    <Form.Check
      type="checkbox"
      className={classNames(
        'form-check mb-0 d-flex align-items-center',
        className,
      )}
    >
      <Form.Check.Input
        type="checkbox"
        className="mt-0"
        ref={resolvedRef}
        {...rest}
      />
    </Form.Check>
  );
});

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

interface TableChildProps {
  table?: boolean;
  getTableProps?: ReturnType<typeof useTable>['getTableProps'];
  headers?: ReturnType<typeof useTable>['headers'];
  page?: Row[];
  prepareRow?: ReturnType<typeof useTable>['prepareRow'];
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  nextPage?: () => void;
  previousPage?: () => void;
  gotoPage?: (page: number) => void;
  pageCount?: number;
  pageIndex?: number;
  selectedRowIds?: Record<string, boolean>;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  globalFilter?: string;
  setGlobalFilter?: (value?: string) => void;
  children?: ReactNode;
}

interface AdvanceTableWrapperProps {
  children: ReactNode;
  sortBy?: SortingRule<Record<string, unknown>>[];
  columns: Column<Record<string, unknown>>[];
  data: Record<string, unknown>[];
  sortable?: boolean;
  selection?: boolean;
  selectionColumnWidth?: number | string;
  selectionHeaderClassname?: string;
  pagination?: boolean;
  perPage?: number;
}

const AdvanceTableWrapper = ({
  children,
  sortBy = [],
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  selectionHeaderClassname,
  pagination,
  perPage = 10,
}: AdvanceTableWrapperProps) => {
  const {
    getTableProps,
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      disableSortBy: !sortable,
      initialState: {
        pageSize: pagination ? perPage : data.length,
        sortBy,
      },
    },
    useGlobalFilter,
    useSortBy,
    useBlockLayout,
    usePagination,
    useRowSelect,
    useFlexLayout,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columnsList => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              className: selectionHeaderClassname,
              style: {
                width: selectionColumnWidth,
              },
            },
            cellProps: {
              style: {
                width: selectionColumnWidth,
              },
            },
            Cell: ({ row }) => (
              <div>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            ),
          },
          ...columnsList,
        ]);
      }
    },
  );

  const tableProps = {
    getTableProps,
    headers,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    gotoPage,
    pageCount,
    pageIndex,
    selectedRowIds,
    pageSize,
    setPageSize,
    globalFilter,
    setGlobalFilter,
  };

  const recursiveMap = (nodes: ReactNode): ReactNode =>
    React.Children.map(nodes, child => {
      if (!isValidElement<TableChildProps>(child)) return child;

      if (child.props?.children) {
        return cloneElement(child, {
          children: recursiveMap(child.props.children),
        });
      }

      if (child.props?.table) {
        return cloneElement(child, {
          ...child.props,
          ...tableProps,
        });
      }

      return child;
    });

  return <>{recursiveMap(children)}</>;
};

export default AdvanceTableWrapper;
